<template>
  <b-modal
    :ok-disabled="formSubmitted"
    id="uploadFileModal"
    :centered="true"
    :title="modalTitle"
    :hide-footer="true"
    @shown="handleModalShown"
    @hidden="hideModal()"
    size="lg"
    close-on-backdrop
  >
    <form>
      <template v-if="row.status === 'submitted' || !isEdit">
        <label for="member" class="font-bold modal-label-prudential">
          Select Member
        </label>
        <span class="input-required">*</span><br />
        <div class="wrapper modal-label-prudential mb-2 pb-1">
          <a-auto-complete
            v-model="member_name"
            :data-source="memberNames"
            placeholder="Select Member"
            @search="onNameSearch"
            @change="onChange"
          />
        </div>
      </template>

      <template>
        <label for="plan" class="font-bold modal-label-prudential">
          {{
            row?.status?.toLowerCase() === "submitted"
              ? "Selected Plan"
              : "Select Plan"
          }}
        </label>
        <span class="input-required">*</span><br />
        <div class="wrapper modal-label-prudential mb-2 pb-1">
          <multiselect
            v-model="form.plan_id"
            placeholder="Select Plan"
            :options="plan_details"
            :disabled="row?.status?.toLowerCase() === 'submitted'"
            track-by="plan_id"
            label="plan_name"
          />
        </div>

        <div v-if="showError('plan_id')" class="error-message">
          Please select a plan
        </div>
      </template>

      <label class="modal-label-prudential" for="file">{{ fileLabel }} </label>
      <template v-if="row?.status?.toLowerCase() !== 'submitted'">
        <span class="input-required"> * </span><br />
        <div 
          class="drop-zone"
          @dragover.prevent="handleDragOver"
          @dragleave.prevent="handleDragLeave"
          @drop.prevent="handleDrop"
          :class="{ 'active-drag': isDragging }"
        >
          <input
            ref="fileInput"
            class="w-100 mb-3"
            type="file"
            id="fileInput"
            name="fileInput"
            multiple
            @change="fileSelected"
            :disabled="row?.status?.toLowerCase() === 'submitted'"
            hidden
          />
          <button
            type="button"
            class="choose-file-btn"
            @click="triggerFileInput"
            :disabled="row?.status?.toLowerCase() === 'submitted'"
          >
            Drop files here or "Browse"
          </button>
        </div>
        <div v-if="showError('files')" class="error-message">
          Please Upload file
        </div>
      </template>

      <div class="d-flex flex-wrap">
        <div
          class="mb-2 file-name-wrap file-name-list pl-2 pb-1"
          v-for="(file, index) in form.files"
          :key="`${file.name}-${index}`"
        >
          <span class="pr-3">{{ file.name }}</span>
          <div
            class="close-icon"
            @click="removeFile(index)"
            v-if="row?.status?.toLowerCase() !== 'submitted'"
          >
            <i class="fas fa-times ml-2"></i>
          </div>
        </div>
      </div>
      <template
        v-if="
          row?.status?.toLowerCase() == 'submitted' &&
          form.file_lists &&
          form.file_lists.length > 0
        "
      >
        <div class="d-flex flex-wrap">
          <div
            class="mb-2 pl-2 file-name-wrap pb-1"
            v-for="file in form.file_lists"
            :key="`${file.filename}`"
          >
            <a
              v-if="row?.status?.toLowerCase() == 'submitted'"
              :href="file.filepath"
              target="_blank"
              class="clr-active"
            >
              <span class="pr-3 pb-4">{{ file.filename }}</span></a
            >
            <span v-else class="pr-3">{{ file.filename }}</span>
            <div class="close-icon" @click="downloadFile(file.filepath)">
              <i
                class="fas fa-download ml-2"
                style="font-size: 10px; position: absolute; top: 5px; left: 5px"
              ></i>
            </div>
          </div>
        </div>
      </template>
      <template
        v-if="
          isEdit &&
          row?.status?.toLowerCase() !== 'submitted' &&
          form.file_lists &&
          form.file_lists.length > 0
        "
      >
        <div class="d-flex flex-wrap edit-icon">
          <div
            class="mb-2 pl-2 file-name-wrap pb-1"
            v-for="(file, index) in form.file_lists"
            :key="`${file.filename}`"
          >
            <span class="pr-3">{{ file.filename }} </span>

            <!-- Removed the wrapping div for the download icon to ensure proper click event handling -->
            <i
              @click="downloadFile(file.filepath)"
              class="fas fa-download ml-2 close-icon"
              style="font-size: 10px; position: absolute; top: 5px; right: 22px"
            >
            </i>

            <i
              @click="removeFileLists(file.id, index)"
              class="fas fa-times ml-2 close-icon"
              style="font-size: 10px; position: absolute; top: 5px"
            >
            </i>
          </div>
        </div>
      </template>

      <template
        v-if="row?.status?.toLowerCase() === 'submitted' && form.description"
      >
        <label class="modal-label-prudential mt-2 mb-1" for="description"
          >Remarks:</label
        ><br />
        <p v-if="row?.status?.toLowerCase() === 'submitted'">
          {{ form.description }}
        </p>
      </template>
      <template v-else-if="row?.status?.toLowerCase() !== 'submitted'">
        <label class="modal-label-prudential" for="description">Remarks:</label
        ><br />
        <textarea
          class="w-100 form-text-desc"
          v-model="form.description"
          rows="6"
        ></textarea>
      </template>
      <div
        v-if="row?.status?.toLowerCase() !== 'submitted'"
        class="submit-btn upload-submit"
      >
        <button @click.prevent="submitFiles">
          {{ isEdit ? "Update" : "Submit" }}
        </button>
      </div>
    </form>
  </b-modal>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import axios from "axios";
import helper from "@/resource/Helper";
import Api from "@/resource/Api";

function initialState() {
  return {
    form: {
      claim_type: "REP",
      plan_id: "",
      files: [],
      file_lists: [],
      description: "",
      deleted_files: [],
    },
    plan_details: [],
    formSubmitted: false,
    originalFileLists: [],
    memberNames: [],
    memberData: [],
    member_name: "",
    hasRunGetPolicyDetails: false,
    debounceTimer: null,
    searchDebounceTimer: null,
    cancelTokenSource: null,
  };
}

export default {
  props: {
    row: { required: false },
    isEdit: { required: false },
  },
  data() {
    return {
      ...initialState(),
      isDragging: false
    };
  },
  customPlanLabel(option) {
    return `${option.plan_name} (${option.plan_id})`;
  },
  watch: {
    isEdit(val) {
      if (val && this.row) {
        this.form.plan_id = this.row.plan_overview;
        this.form.files = [];
        this.form.file_lists = this.row.files;
        this.form.description = this.row?.description || "";
      }
    },
    row(val) {
      if (this.isEdit && val) {
        this.form.plan_id = val.plan_overview;
        this.form.files = [];
        this.form.file_lists = val.files;
        this.originalFileLists = val.files;
        this.form.description = this.row.description || "";
      }
    },
  },
  computed: {
    fileLabel() {
      return this.row?.status?.toLowerCase() === "submitted"
        ? "Uploaded File(s):"
        : "Upload File(s):";
    },
    modalTitle() {
      if (this.row?.status?.toLowerCase() === "submitted") {
        return "View Claim Information";
      } else {
        return this.isEdit ? "Edit Claim Information" : "Add Claim Information";
      }
    },
    selectedPolicyId() {
      if (!this.form.plan_id) return null;
      const selectedPlan = this.plan_details.find(
        (plan) =>
          plan.plan_id ===
          (typeof this.form.plan_id === "object"
            ? this.form.plan_id.plan_id
            : this.form.plan_id)
      );
      return selectedPlan?.policy_id || null;
    },
    selectedMember() {
      return this.memberData.find(
        (member) => member.formatted_name === this.member_name
      );
    },
  },

  methods: {
    onNameSearch(searchText) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        if (searchText.length > 3) {
          this.cancelPendingRequest();
          this.fetchMemberNames(searchText);
        } else {
          this.memberNames = [];
        }
      }, 300);
    },
    onChange(value) {
      if (value == "") {
        this.memberNames = [];
      }
      clearTimeout(this.debounceTimer);

      // Set a new timer with a 300ms delay
      this.debounceTimer = setTimeout(() => {
        if (this.hasRunGetPolicyDetails) {
          this.getPolicyDetails();
        } else {
          this.hasRunGetPolicyDetails = true;
        }
      }, 300);
    },
    async fetchMemberNames(searchText) {
      try {
        this.cancelTokenSource = axios.CancelToken.source();
        
        const { data } = await Api.get(`v2/get-prudential-member?member_name=${searchText}`, {
          cancelToken: this.cancelTokenSource.token
        });

        if (data.data) {
          this.memberNames = [...new Set(data.data.map(item => item.formatted_name))];
          this.memberData = data.data;
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          this.memberNames = [];
          console.error("Error fetching members:", error);
        }
      }
    },
    cancelPendingRequest() {
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel("Operation canceled by new request");
      }
    },
    handleModalShown() {
      document.body.style.overflow = 'hidden';
      if (this.row?.status?.toLowerCase() === "submitted" || this.isEdit) {
        this.getPolicyDetails();
      }
    },

    handleDragOver(event) {
      event.preventDefault();
      if (!this.isDragging) {
        this.isDragging = true;
      }
      event.dataTransfer.dropEffect = 'copy';
    },

    handleDragLeave(event) {
      event.preventDefault();
      this.isDragging = false;
    },

    handleDrop(event) {
      event.preventDefault();
      this.isDragging = false;
      
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.form.files = this.form.files.concat(Array.from(files));
      }
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    downloadFile(filePath) {
      const link = document.createElement("a");
      link.href = filePath;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    resetData() {
      Object.assign(this.$data, initialState());
    },
    hideModal() {
      clearTimeout(this.debounceTimer);
      clearTimeout(this.searchDebounceTimer);
      this.cancelPendingRequest();
      this.resetData();
      this.$v.form.$reset();
      this.$emit("update-isEdit", false);
      document.body.style.overflow = '';
      this.$bvModal.hide("uploadFileModal");
    },
    removeFile(index) {
      this.form.files.splice(index, 1);
    },
    removeFileLists(id, index) {
      this.form.deleted_files.push(id);
      this.form.file_lists.splice(index, 1);
    },
    getPolicyDetails() {
      let loader = this.$loading.show();
      let memberId = this.row?.claim_for || this.selectedMember?.member_id;
      let url = `v2/prudential-claim/plan-list?user_id=${memberId}`;
      axios
        .get(url)
        .then((res) => {
          if (res.data.status === "success") {
            this.plan_details = res.data.data;
            if (this.isEdit && this.row?.files) {
              this.form.file_lists = [...this.row.files];
            } else {
              this.form.file_lists = [...this.originalFileLists];
            }
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          loader.hide();
        });
    },
    fileSelected(event) {
      this.form.files = this.form.files.concat(Array.from(event.target.files));
    },
    submitFiles() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      let form = this.isEdit
        ? {
            ...this.form,
            policy_id: this.selectedPolicyId || this.row.policy_id,
            claim_id: this.row.id,
            plan_id:
              typeof this.form.plan_id === "object"
                ? this.form.plan_id.plan_id || this.form.plan_id.pid
                : this.form.plan_id.pid,
            _method: "PUT",
          }
        : {
            ...this.form,
            policy_id: this.selectedPolicyId,
            user_id: this.row.claim_for || this.selectedMember.member_id,
            plan_id:
              typeof this.form.plan_id === "object"
                ? this.form.plan_id.plan_id || this.form.plan_id.pid
                : this.form.plan_id,
          };

      this.formSubmitted = true;
      let formData = new FormData();
      for (const [key, value] of Object.entries(form)) {
        if (Array.isArray(value)) {
          form[key].forEach((data, index) => {
            formData.append(`${key}[${index}]`, data);
          });
        } else {
          formData.append(key, value);
        }
      }
      if (this.form.deleted_files && this.form.deleted_files.length <= 0) {
        formData.append("deleted_files[0]", null);
      }
      let url = `v2/prudential-claim/${this.isEdit ? "update" : "store"}`;
      this.save(url, formData);
    },
    save(url, formData) {
      let loader = this.$loading.show();
      axios
        .post(url, formData)
        .then((res) => {
          if (res.data.status === "success") {
            helper.infoMessage(this, res.data.status, res.data.message);
            this.$parent.getData();
            this.hideModal();
          }
        })
        .catch((err) => {
          if (err.response && err.response.data) {
            helper.infoMessage(this, "error", err.response.data.message);
          } else {
            helper.infoMessage(this, "error", "Something went wrong. Please try again.");
          }
          this.hideModal();
        })
        .finally(() => {
          this.formSubmitted = false;
          loader.hide();
        });
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    showError(name) {
      return this.$v.form[name].$error;
    },
  },
  validations: {
    form: {
      plan_id: { required },
      files: {
        required(value, form) {
          return form.file_lists.length > 0 || (value && value.length > 0);
        },
      },
    },
  },
};
</script>

<style scoped>

.drop-zone {
  border: 2px dashed #ccc;
  border-radius: 5px;
  padding: 20px;
  text-align: center;
  margin-bottom: 1rem;
  transition: all 0.3s ease;
}

.active-drag {
  border-color: #007bff;
  background-color: rgba(0, 123, 255, 0.1);
}

.choose-file-btn {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  font-size: 1rem;
  padding: 5px 10px;
}

.choose-file-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.error-message {
  margin-top: -8px;
  margin-bottom: 10px;
  color: red;
  font-size: 12px;
}
.file-name-wrap {
  margin-right: 2%;
}
.form-text-desc {
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  margin-top: 0.18rem;
  padding: 0.8rem;
  font-size: 14px;
}
.edit-icon .file-name-wrap {
  padding-right: 3.4rem;
}
.edit-icon .fa-download::before {
  position: absolute;
  left: 0.3rem;
  top: 0.25rem;
}

.edit-icon .fa-times::before {
  position: absolute;
  left: 0.4rem;
  top: 0.3rem;
}
</style>
